import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Clock, AlertTriangle, CalendarClock, ChevronRight, UserPlus, Filter, Flame, ArrowRight, ShieldCheck,
} from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { api, apiError, peso, timeAgo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const PIPELINE = ["NEW", "CONTACTED", "QUALIFIED", "VIEWING", "NEGOTIATING", "RESERVED"];
export const STATUS_LABEL = {
  NEW: "New Lead", CONTACTED: "Contacted", QUALIFIED: "Qualified", VIEWING: "Viewing",
  NEGOTIATING: "Negotiating", RESERVED: "Reserved", CLOSED: "Closed", LOST: "Lost",
};
export const LEAD_TYPES = ["BUYER", "RENTER", "SELLER", "LANDLORD", "OTHER"];
export const SOURCES = ["MARKETPLACE_INQUIRY", "BUYER_REQUIREMENT", "PROPERTY_INQUIRY", "PROFILE_INQUIRY", "REFERRAL", "MANUAL", "OTHER"];
const SOURCE_LABEL = {
  MARKETPLACE_INQUIRY: "Marketplace", BUYER_REQUIREMENT: "Buyer Need",
  PROPERTY_INQUIRY: "Property Inquiry", PROFILE_INQUIRY: "Profile Inquiry", REFERRAL: "Referral", MANUAL: "Manual", OTHER: "Other",
};

const TYPE_TONE = {
  BUYER: "bg-[#0F2C59]/10 text-[#0F2C59]", RENTER: "bg-teal-600/10 text-teal-700",
  SELLER: "bg-[#C79A3B]/15 text-[#8a6a1f]", LANDLORD: "bg-indigo-600/10 text-indigo-700",
  OTHER: "bg-slate-200 text-slate-600",
};
const PRIORITY_TONE = { HIGH: "text-critical", MEDIUM: "text-accent", LOW: "text-slate-400" };
const BUCKET = {
  overdue: { label: "Overdue", cls: "bg-critical-soft text-critical", icon: AlertTriangle },
  due_today: { label: "Due today", cls: "bg-attention-soft text-attention", icon: CalendarClock },
  upcoming: { label: "Upcoming", cls: "bg-info-soft text-info", icon: Clock },
};

function budgetLabel(l) {
  if (!l.budget_min && !l.budget_max) return null;
  if (l.budget_min && l.budget_max) return `${peso(l.budget_min)}–${peso(l.budget_max)}`;
  return peso(l.budget_max || l.budget_min);
}

function LeadCard({ lead, onStatusChange, onOpen }) {
  const bucket = lead.follow_up_bucket ? BUCKET[lead.follow_up_bucket] : null;
  const budget = budgetLabel(lead);
  return (
    <div data-testid={`lead-card-${lead.id}`}
      className="bg-card border border-border rounded-xl p-3 hover:shadow-md transition-shadow cursor-pointer group"
      onClick={() => onOpen(lead.id)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {lead.priority === "HIGH" && <Flame className={`h-3.5 w-3.5 shrink-0 ${PRIORITY_TONE.HIGH}`} />}
            <span className="font-semibold text-slate-900 truncate" title={lead.contact_name}>{lead.contact_name}</span>
          </div>
          <span className={`inline-block mt-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${TYPE_TONE[lead.lead_type] || TYPE_TONE.OTHER}`}>
            {lead.lead_type}
          </span>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
      </div>
      {(lead.preferred_location || budget) && (
        <div className="mt-2 text-xs text-slate-500 space-y-0.5">
          {lead.preferred_location && <div className="truncate">📍 {lead.preferred_location}</div>}
          {budget && <div className="font-medium text-slate-700">{budget}</div>}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="text-slate-400">{SOURCE_LABEL[lead.source] || lead.source}</span>
        {lead.inquiry_count > 0 && <span className="text-slate-400">· {lead.inquiry_count} inquir{lead.inquiry_count === 1 ? "y" : "ies"}</span>}
        {bucket && (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium ${bucket.cls}`}>
            <bucket.icon className="h-3 w-3" />{bucket.label}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
        <Select value={lead.status} onValueChange={(v) => onStatusChange(lead, v)}>
          <SelectTrigger data-testid={`lead-status-${lead.id}`} className="h-7 text-xs w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[...PIPELINE, "CLOSED", "LOST"].map((s) => <SelectItem key={s} value={s} className="text-xs">{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(lead.last_activity_at)}</span>
      </div>
    </div>
  );
}

function NewLeadDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ contact_name: "", lead_type: "BUYER", source: "MANUAL", contact_email: "", contact_mobile: "", preferred_location: "", budget_min: "", budget_max: "", priority: "MEDIUM" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.contact_name.trim()) { toast.error("Contact name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, budget_min: f.budget_min ? Number(f.budget_min) : null, budget_max: f.budget_max ? Number(f.budget_max) : null };
      const { data } = await api.post("/leads", payload);
      toast.success("Lead created");
      onOpenChange(false);
      onCreated(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><UserPlus className="h-5 w-5 text-accent" /> New Lead</DialogTitle>
          <DialogDescription>Add a person you're working. Link a Buyer Need or property later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="new-lead-name" placeholder="Contact name *" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={f.lead_type} onValueChange={(v) => set("lead_type", v)}>
              <SelectTrigger data-testid="new-lead-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="new-lead-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{["HIGH", "MEDIUM", "LOW"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input data-testid="new-lead-email" placeholder="Email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
          <Input data-testid="new-lead-mobile" placeholder="Mobile (e.g. +63…)" value={f.contact_mobile} onChange={(e) => set("contact_mobile", e.target.value)} />
          <Input data-testid="new-lead-location" placeholder="Preferred location" value={f.preferred_location} onChange={(e) => set("preferred_location", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="new-lead-budget-min" type="number" placeholder="Budget min" value={f.budget_min} onChange={(e) => set("budget_min", e.target.value)} />
            <Input data-testid="new-lead-budget-max" type="number" placeholder="Budget max" value={f.budget_max} onChange={(e) => set("budget_max", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button data-testid="new-lead-submit" onClick={submit} disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90">Create Lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Leads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("ALL");
  const [sourceF, setSourceF] = useState("ALL");
  const [dueF, setDueF] = useState("ALL");
  const [showLost, setShowLost] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_terminal: showLost };
      if (typeF !== "ALL") params.lead_type = typeF;
      if (sourceF !== "ALL") params.source = sourceF;
      if (dueF !== "ALL") params.follow_up_due = dueF;
      if (q.trim()) params.q = q.trim();
      const [{ data: ls }, { data: sm }] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/summary"),
      ]);
      setLeads(ls); setSummary(sm);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [typeF, sourceF, dueF, showLost]);

  const changeStatus = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success(`Moved to ${STATUS_LABEL[status]}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const columns = showLost ? [...PIPELINE, "CLOSED", "LOST"] : PIPELINE;
  const byStatus = useMemo(() => {
    const m = {}; columns.forEach((c) => (m[c] = []));
    leads.forEach((l) => { if (m[l.status]) m[l.status].push(l); });
    return m;
  }, [leads, columns]);

  const kpi = summary?.kpis || {};

  return (
    <PageShell testid="leads-page">
      <Fade>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Leads</h1>
            <p className="text-sm text-slate-500 mt-0.5">Your opportunity pipeline — from first inquiry to close.</p>
          </div>
          <Button data-testid="new-lead-btn" onClick={() => setNewOpen(true)} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl font-semibold shrink-0">
            <Plus className="h-4 w-4 mr-1.5" /> New Lead
          </Button>
        </div>

        {/* Privacy / trust reassurance — secondary to the CRM itself */}
        <div data-testid="leads-privacy-note" className="mb-4 flex items-start gap-2.5 rounded-lg border border-[#0F2C59]/10 bg-[#0F2C59]/[0.03] px-3 py-2">
          <ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />
          <p className="text-[12px] leading-relaxed text-slate-500">
            <span className="font-semibold text-[#0F2C59]">Your leads. Your clients. Your business.</span>{" "}
            Your client and lead information is private to your professional workspace. Restricted administrative access is controlled and audited.
          </p>
        </div>

        {/* Follow-up + KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div data-testid="leads-kpi-active" className="bg-card border border-border rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-slate-900">{kpi.active_leads ?? "—"}</div>
            <div className="text-xs text-slate-500">Active leads</div>
          </div>
          <div data-testid="leads-kpi-followups" className="bg-card border border-border rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-critical">{kpi.follow_ups_due ?? "—"}</div>
            <div className="text-xs text-slate-500">Follow-ups due</div>
          </div>
          <div data-testid="leads-kpi-qualified" className="bg-card border border-border rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-accent">{kpi.qualified_leads ?? "—"}</div>
            <div className="text-xs text-slate-500">Qualified</div>
          </div>
          <div className="bg-card border border-border rounded-xl px-4 py-3">
            <div className="text-2xl font-bold text-slate-900">{leads.length}</div>
            <div className="text-xs text-slate-500">Showing</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="leads-search" placeholder="Search name, email, mobile…" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9" />
          </div>
          <Select value={typeF} onValueChange={setTypeF}>
            <SelectTrigger data-testid="leads-filter-type" className="w-[130px]"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All types</SelectItem>{LEAD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sourceF} onValueChange={setSourceF}>
            <SelectTrigger data-testid="leads-filter-source" className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All sources</SelectItem>{SOURCES.map((s) => <SelectItem key={s} value={s}>{SOURCE_LABEL[s]}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={dueF} onValueChange={setDueF}>
            <SelectTrigger data-testid="leads-filter-due" className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any follow-up</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="due_today">Due today</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="leads-toggle-lost" variant={showLost ? "default" : "outline"} onClick={() => setShowLost((v) => !v)}
            className={`rounded-xl ${showLost ? "bg-primary text-primary-foreground" : ""}`}>
            {showLost ? "Hide Closed/Lost" : "Show Closed/Lost"}
          </Button>
        </div>

        {/* Pipeline board — horizontal scroll (deliberate), contained (no document overflow) */}
        {loading ? (
          <p className="text-slate-500 text-sm" data-testid="leads-loading">Loading pipeline…</p>
        ) : (
          <>
            {/* Mobile discoverability: columns peek + snap + an explicit swipe hint. */}
            <div className="flex sm:hidden items-center gap-1.5 text-[11px] font-medium text-slate-400 mb-1.5 px-1" data-testid="pipeline-swipe-hint">
              <span>Swipe to see all {columns.length} stages</span>
              <ArrowRight className="h-3.5 w-3.5 animate-pulse" />
            </div>
            <div className="overflow-x-auto pb-2 pc-scroll snap-x snap-mandatory sm:snap-none" data-testid="leads-pipeline">
              <div className="flex gap-3 min-w-max">
                {columns.map((col) => (
                  <div key={col} data-testid={`leads-col-${col}`} className="w-[80vw] max-w-[300px] sm:w-[260px] shrink-0 snap-start">
                    <div className="flex items-center justify-between px-1 mb-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-600">{STATUS_LABEL[col]}</span>
                      <span className="text-xs font-semibold text-slate-400 bg-secondary rounded-full px-2 py-0.5">{byStatus[col]?.length || 0}</span>
                    </div>
                    <div className="space-y-2 min-h-[64px] bg-secondary/40 rounded-xl p-2">
                      {(byStatus[col] || []).map((l) => (
                        <LeadCard key={l.id} lead={l} onStatusChange={changeStatus} onOpen={(id) => nav(`/leads/${id}`)} />
                      ))}
                      {(byStatus[col] || []).length === 0 && (
                        <p className="text-[11px] text-slate-300 text-center py-3">—</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </Fade>
      <NewLeadDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => nav(`/leads/${id}`)} />
    </PageShell>
  );
}
